import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Building,
  Layers,
  Settings,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Image as ImageIcon,
  X,
  Upload,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
  FileUp,
} from "lucide-react";
import {
  User,
  CreateUserRequest,
  Room,
  RoomCreate,
  GlobalSettings,
  RoomSection,
  SectionExpectedItemCreate,
  RoomSectionCreate,
} from "@/types";
import { apiService } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useCategoryContext } from "@/contexts/CategoryContext";
import { getClassColor, colorWithOpacity } from "@/utils/colorUtils";
import { formatClassNameForDisplay } from "@/utils/classNameUtils";
import ThumbnailPreview from "@/components/ThumbnailPreview";
import { CreateSectionModal } from "@/components/section/CreateSectionModal";
import { createPortal } from "react-dom";

type TabType = "users" | "rooms" | "settings";

// Fuzzy matching utilities
function fuzzyScore(query: string, candidate: string): number {
  const q = query.trim().toLowerCase();
  const c = candidate.toLowerCase();
  if (!q) return 0;
  if (c.startsWith(q)) return 1000 - (c.length - q.length);
  const idx = c.indexOf(q);
  if (idx !== -1) return 800 - idx;
  let last = -1;
  let score = 0;
  let streak = 0;
  for (const ch of q) {
    const pos = c.indexOf(ch, last + 1);
    if (pos === -1) return -1;
    streak = pos === last + 1 ? streak + 1 : 1;
    score += 10 * streak;
    if (pos === 0 || /[^a-z0-9]/.test(c[pos - 1])) score += 15;
    last = pos;
  }
  return score;
}

function fuzzyFilter(items: string[], query: string): string[] {
  if (!query.trim()) return items;
  return items
    .map((name) => ({ name, s: fuzzyScore(query, name) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.name);
}

const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL
  const getTabFromPath = (): TabType => {
    if (location.pathname === "/admin/rooms") return "rooms";
    if (location.pathname === "/admin/settings") return "settings";
    return "users";
  };

  const [activeTab, setActiveTab] = useState<TabType>(getTabFromPath());

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath());
  }, [location.pathname]);

  // Handle tab change with URL update
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "users") navigate("/admin/users");
    else if (tab === "rooms") navigate("/admin/rooms");
    else if (tab === "settings") navigate("/admin/settings");
  };

  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Room management state
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomSections, setRoomSections] = useState<Record<number, RoomSection[]>>({});
  const [expandedRooms, setExpandedRooms] = useState<Set<number>>(new Set());
  const [roomLoading, setRoomLoading] = useState(false);
  const [roomError, setRoomError] = useState("");
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [showCreateSectionModal, setShowCreateSectionModal] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<RoomSection | null>(null);
  const [availableClasses, setAvailableClasses] = useState<string[]>([]);

  // Import/Export state
  const [showImportExportMenu, setShowImportExportMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importValidation, setImportValidation] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [csvContent, setCsvContent] = useState("");
  const importExportRef = useRef<HTMLDivElement>(null);

  // Drag and drop state
  const [draggingSectionId, setDraggingSectionId] = useState<number | null>(null);
  const [dragOverRoomId, setDragOverRoomId] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Global settings state
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSyncing, setSettingsSyncing] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllUsers();
      setUsers(response.users);
    } catch (error: any) {
      console.error("Failed to fetch users:", error);
      setError(t("admin.failed_to_load"));
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      setRoomLoading(true);
      setRoomError("");
      const response = await apiService.getRooms();
      setRooms(response.rooms);
      // Auto-expand all rooms and fetch their sections
      const roomIds = new Set(response.rooms.map((r: Room) => r.id));
      setExpandedRooms(roomIds);
      // Fetch sections for all rooms
      response.rooms.forEach((room: Room) => {
        fetchRoomSections(room.id);
      });
    } catch (error: any) {
      console.error("Failed to fetch rooms:", error);
      setRoomError(t("admin_rooms.failed_to_load"));
    } finally {
      setRoomLoading(false);
    }
  };

  const fetchRoomSections = async (roomId: number) => {
    try {
      const sections = await apiService.getRoomSections(roomId);
      const sorted = sections.sort((a: RoomSection, b: RoomSection) => a.display_order - b.display_order);
      setRoomSections((prev) => ({ ...prev, [roomId]: sorted }));
    } catch (error) {
      console.error("Failed to fetch sections for room", roomId, error);
    }
  };

  const fetchAvailableClasses = async () => {
    try {
      const response = await apiService.getEnrollmentClasses();
      const classes = response.map((cls: any) => cls.folderName);
      setAvailableClasses(classes);
    } catch (error) {
      console.error("Failed to fetch available classes:", error);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      setSettingsLoading(true);
      setSettingsError("");
      const settings = await apiService.getGlobalSettings();
      setGlobalSettings(settings);
    } catch (error: any) {
      console.error("Failed to fetch global settings:", error);
      setSettingsError("Failed to load global settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!globalSettings) return;

    try {
      setSettingsSaving(true);
      setSettingsError("");
      setSettingsSuccess("");

      const updated = await apiService.updateGlobalSettings({
        conf_threshold: globalSettings.conf_threshold,
        iou_threshold: globalSettings.iou_threshold,
        yolo_img_size: globalSettings.yolo_img_size,
        cutoff_threshold: globalSettings.cutoff_threshold,
        top_distances: globalSettings.top_distances,
        major_votes: globalSettings.major_votes,
        show_uncertain_detections: globalSettings.show_uncertain_detections,
        refiner_enabled: globalSettings.refiner_enabled,
        refiner_threshold: globalSettings.refiner_threshold,
        refiner_trigger_threshold: globalSettings.refiner_trigger_threshold,
        disable_manual_detection_discard: globalSettings.disable_manual_detection_discard,
      });

      setGlobalSettings(updated);
      setSettingsSuccess("Settings saved successfully!");
      setTimeout(() => setSettingsSuccess(""), 3000);
    } catch (error: any) {
      console.error("Failed to save global settings:", error);
      setSettingsError(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSyncFromEnv = async () => {
    if (!confirm("This will reset all settings to their default values from the environment configuration. Continue?")) {
      return;
    }

    try {
      setSettingsSyncing(true);
      setSettingsError("");
      setSettingsSuccess("");

      const synced = await apiService.syncGlobalSettingsFromEnv();
      setGlobalSettings(synced);
      setSettingsSuccess("Settings reset to defaults successfully!");
      setTimeout(() => setSettingsSuccess(""), 3000);
    } catch (error: any) {
      console.error("Failed to reset settings:", error);
      setSettingsError(error.response?.data?.detail || "Failed to reset settings to defaults");
    } finally {
      setSettingsSyncing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRooms();
    fetchGlobalSettings();
    fetchAvailableClasses();
  }, []);

  const toggleRoomExpanded = (roomId: number) => {
    setExpandedRooms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roomId)) {
        newSet.delete(roomId);
      } else {
        newSet.add(roomId);
        // Fetch sections if not already loaded
        if (!roomSections[roomId]) {
          fetchRoomSections(roomId);
        }
      }
      return newSet;
    });
  };

  const handleDeleteUser = async (userId: number) => {
    if (userId === currentUser?.id) {
      alert(t("admin.cannot_delete_self"));
      return;
    }

    if (confirm(t("admin.delete_confirmation"))) {
      try {
        await apiService.deleteUser(userId);
        setUsers(users.filter((u) => u.id !== userId));
      } catch (error: any) {
        console.error("Failed to delete user:", error);
        alert(t("admin.delete_failed"));
      }
    }
  };

  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    if (userId === currentUser?.id) {
      alert("You cannot deactivate your own account");
      return;
    }

    try {
      await apiService.updateUser(userId, { is_active: !isActive });
      setUsers(
        users.map((u) =>
          u.id === userId ? { ...u, is_active: !isActive } : u
        )
      );
    } catch (error: any) {
      console.error("Failed to update user status:", error);
      alert("Failed to update user status");
    }
  };

  const handleDeleteRoom = async (roomId: number) => {
    if (confirm(t("admin_rooms.delete_confirmation"))) {
      try {
        await apiService.deleteRoom(roomId);
        fetchRooms();
      } catch (error: any) {
        console.error("Failed to delete room:", error);
        alert(t("admin_rooms.delete_failed"));
      }
    }
  };

  const handleDeleteSection = async (sectionId: number, roomId: number) => {
    if (confirm("Are you sure you want to delete this section?")) {
      try {
        await apiService.deleteSection(sectionId);
        fetchRoomSections(roomId);
      } catch (error: any) {
        console.error("Failed to delete section:", error);
        alert("Failed to delete section");
      }
    }
  };

  // Import/Export handlers
  const handleExportLayout = async () => {
    try {
      const blob = await apiService.exportLayout();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "layout_export.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowImportExportMenu(false);
    } catch (error: any) {
      console.error("Failed to export layout:", error);
      alert("Failed to export layout");
    }
  };

  const handleExportExample = async () => {
    try {
      const blob = await apiService.exportExampleLayout();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "layout_example.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowImportExportMenu(false);
    } catch (error: any) {
      console.error("Failed to export example:", error);
      alert("Failed to export example");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      setCsvContent(content);
      setImportError("");
      setImportValidation(null);

      try {
        setImportLoading(true);
        const validation = await apiService.validateLayoutImport(content);
        setImportValidation(validation);
      } catch (error: any) {
        console.error("Failed to validate CSV:", error);
        setImportError(error.response?.data?.detail || "Failed to validate CSV");
      } finally {
        setImportLoading(false);
      }
    };
    reader.readAsText(file);
  };

  const handleImportLayout = async (clearExisting: boolean = false) => {
    if (!importValidation?.preview_data) return;

    const rows = importValidation.preview_data
      .filter((row: any) => row.valid)
      .map((row: any) => ({
        room_name: row.room_name,
        section_name: row.section_name,
        section_description: row.section_description,
        class_name: row.class_name,
        expected_quantity: row.expected_quantity,
      }));

    if (rows.length === 0) {
      setImportError("No valid rows to import");
      return;
    }

    try {
      setImportLoading(true);
      setImportError("");
      const result = await apiService.importLayout(rows, clearExisting);
      setImportSuccess(result.message);
      setShowImportModal(false);
      setImportValidation(null);
      setCsvContent("");
      fetchRooms();
      // Refresh all expanded room sections
      for (const roomId of expandedRooms) {
        fetchRoomSections(roomId);
      }
    } catch (error: any) {
      console.error("Failed to import layout:", error);
      setImportError(error.response?.data?.detail || "Failed to import layout");
    } finally {
      setImportLoading(false);
    }
  };

  // Close import/export menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (importExportRef.current && !importExportRef.current.contains(e.target as Node)) {
        setShowImportExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, sectionId: number, sourceRoomId: number) => {
    e.dataTransfer.setData("sectionId", sectionId.toString());
    e.dataTransfer.setData("sourceRoomId", sourceRoomId.toString());
    setDraggingSectionId(sectionId);
  };

  const handleDragEnd = () => {
    setDraggingSectionId(null);
    setDragOverRoomId(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, roomId: number, index: number) => {
    e.preventDefault();
    setDragOverRoomId(roomId);
    setDragOverIndex(index);
  };

  const handleDrop = async (e: React.DragEvent, targetRoomId: number, targetIndex: number) => {
    e.preventDefault();
    const sectionId = parseInt(e.dataTransfer.getData("sectionId"));
    const sourceRoomId = parseInt(e.dataTransfer.getData("sourceRoomId"));

    setDraggingSectionId(null);
    setDragOverRoomId(null);
    setDragOverIndex(null);

    if (!sectionId) return;

    try {
      await apiService.moveSection(sectionId, targetRoomId, targetIndex);
      // Refresh both source and target rooms
      fetchRoomSections(sourceRoomId);
      if (sourceRoomId !== targetRoomId) {
        fetchRoomSections(targetRoomId);
      }
      fetchRooms(); // Update section counts
    } catch (error: any) {
      console.error("Failed to move section:", error);
      alert(error.response?.data?.detail || "Failed to move section");
    }
  };

  if (loading && roomLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif font-bold text-gray-900">
            {t("admin.title")}
          </h1>
          <p className="text-gray-600">{t("admin.subtitle")}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange("users")}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "users"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            User Management
          </button>
          <button
            onClick={() => handleTabChange("rooms")}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "rooms"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Building className="h-4 w-4 inline mr-2" />
            Rooms & Sections
          </button>
          <button
            onClick={() => handleTabChange("settings")}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "settings"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Global Settings
          </button>
        </nav>
      </div>

      {/* Users Tab Content */}
      {activeTab === "users" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t("admin.create_user")}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {users.map((user) => (
                <li
                  key={user.id}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-medium text-gray-900">
                            {user.username}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === "admin"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {user.role}
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="text-indigo-600 hover:text-indigo-900 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          handleToggleUserStatus(user.id, user.is_active)
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          user.is_active
                            ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                            : "text-green-600 hover:text-green-900 hover:bg-green-50"
                        } ${user.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                        title={
                          user.is_active ? "Deactivate user" : "Activate user"
                        }
                        disabled={user.id === currentUser?.id}
                      >
                        {user.is_active ? (
                          <UserX className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className={`text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors ${
                          user.id === currentUser?.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        title="Delete user"
                        disabled={user.id === currentUser?.id}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Rooms Tab Content */}
      {activeTab === "rooms" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            {/* Import success message */}
            {importSuccess && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{importSuccess}</span>
              </div>
            )}
            <div className="flex-1" />
            <div className="flex gap-2">
              {/* Import/Export Dropdown */}
              <div className="relative" ref={importExportRef}>
                <button
                  onClick={() => setShowImportExportMenu(!showImportExportMenu)}
                  className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  Import/Export
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showImportExportMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <button
                      onClick={handleExportLayout}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Export Current Layout
                    </button>
                    <button
                      onClick={handleExportExample}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Export Example Template
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => {
                        setShowImportModal(true);
                        setShowImportExportMenu(false);
                        setImportValidation(null);
                        setCsvContent("");
                        setImportError("");
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileUp className="h-4 w-4" />
                      Import Custom Layout
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowCreateRoomModal(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add New Room
              </button>
            </div>
          </div>

          {roomError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{roomError}</p>
            </div>
          )}

          {rooms.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No rooms created yet</p>
              <p className="text-gray-400 text-sm">
                Create your first room to get started
              </p>
            </div>
          ) : (
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {rooms.map((room) => (
                  <RoomItem
                    key={room.id}
                    room={room}
                    sections={roomSections[room.id] || []}
                    isExpanded={expandedRooms.has(room.id)}
                    onToggle={() => toggleRoomExpanded(room.id)}
                    onEditRoom={() => setEditingRoom(room)}
                    onDeleteRoom={() => handleDeleteRoom(room.id)}
                    onAddSection={() => setShowCreateSectionModal(room.id)}
                    onEditSection={(section) => setEditingSection(section)}
                    onDeleteSection={(sectionId) => handleDeleteSection(sectionId, room.id)}
                    onRefreshSections={() => fetchRoomSections(room.id)}
                    draggingSectionId={draggingSectionId}
                    dragOverRoomId={dragOverRoomId}
                    dragOverIndex={dragOverIndex}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab Content */}
      {activeTab === "settings" && (
        <div className="space-y-6">
          {/* Slider styles */}
          <style>
            {`
              .settings-slider {
                -webkit-appearance: none;
                appearance: none;
                height: 8px;
                border-radius: 4px;
                outline: none;
                background: linear-gradient(90deg, #ff0000 0%, #ffff00 50%, #00ff00 100%);
              }
              .settings-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #ffffff;
                cursor: pointer;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                border: 2px solid #4F46E5;
              }
              .settings-slider::-moz-range-thumb {
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #ffffff;
                cursor: pointer;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                border: 2px solid #4F46E5;
              }
            `}
          </style>

          {/* Header with Save/Refresh buttons */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Default Analysis Settings</h2>
              <p className="text-sm text-gray-600">
                These settings will be used as defaults for all new sessions.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchGlobalSettings}
                disabled={settingsLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${settingsLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                onClick={handleSyncFromEnv}
                disabled={settingsSyncing || settingsLoading}
                className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 flex items-center gap-2"
                title="Reset settings to environment defaults"
              >
                <Download className={`h-4 w-4 ${settingsSyncing ? "animate-bounce" : ""}`} />
                {settingsSyncing ? "Resetting..." : "Reset to Defaults"}
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={settingsSaving || !globalSettings}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {settingsSaving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </div>

          {/* Success/Error Messages */}
          {settingsSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700">{settingsSuccess}</p>
            </div>
          )}
          {settingsError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{settingsError}</p>
            </div>
          )}

          {settingsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : globalSettings ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Detection Settings */}
              <div className="bg-blue-50 rounded-lg p-5 space-y-5">
                <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Detection
                </h3>

                {/* Confidence Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confidence Threshold
                  </label>
                  <input
                    type="range"
                    min="0.01"
                    max="1"
                    step="0.01"
                    value={globalSettings.conf_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      conf_threshold: parseFloat(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(globalSettings.conf_threshold * 100).toFixed(0)}%
                  </div>
                </div>

                {/* IOU Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IOU Threshold
                  </label>
                  <input
                    type="range"
                    min="0.01"
                    max="1"
                    step="0.01"
                    value={globalSettings.iou_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      iou_threshold: parseFloat(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(globalSettings.iou_threshold * 100).toFixed(0)}%
                  </div>
                </div>

                {/* YOLO Image Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detection Image Size
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setGlobalSettings({
                        ...globalSettings,
                        yolo_img_size: Math.max(640, globalSettings.yolo_img_size - 64)
                      })}
                      disabled={globalSettings.yolo_img_size <= 640}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-lg font-medium">{globalSettings.yolo_img_size}</span>
                      <div className="text-xs text-gray-500">pixels</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setGlobalSettings({
                        ...globalSettings,
                        yolo_img_size: Math.min(6400, globalSettings.yolo_img_size + 64)
                      })}
                      disabled={globalSettings.yolo_img_size >= 6400}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Disable Manual Detection Discard Toggle */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Disable Manual Detection Discard
                    </label>
                    <button
                      type="button"
                      onClick={() => setGlobalSettings({
                        ...globalSettings,
                        disable_manual_detection_discard: !globalSettings.disable_manual_detection_discard
                      })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        globalSettings.disable_manual_detection_discard ? "bg-primary-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                          globalSettings.disable_manual_detection_discard ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {globalSettings.disable_manual_detection_discard
                      ? "Manual detections won't be discarded"
                      : "Manual detections may be discarded based on thresholds"}
                  </div>
                </div>
              </div>

              {/* Refinement Settings */}
              <div className="bg-amber-50 rounded-lg p-5 space-y-5">
                <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Refinement
                </h3>

                {/* Enable Refiner Toggle */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Enable Quality Assessment
                    </label>
                    <button
                      type="button"
                      onClick={() => setGlobalSettings({
                        ...globalSettings,
                        refiner_enabled: !globalSettings.refiner_enabled
                      })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        globalSettings.refiner_enabled ? "bg-primary-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                          globalSettings.refiner_enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Refiner Threshold */}
                <div className={globalSettings.refiner_enabled ? "" : "opacity-50 pointer-events-none"}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quality Threshold
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={globalSettings.refiner_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      refiner_threshold: parseFloat(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(globalSettings.refiner_threshold * 100).toFixed(0)}%
                  </div>
                </div>

                {/* Refiner Trigger Threshold */}
                <div className={globalSettings.refiner_enabled ? "" : "opacity-50 pointer-events-none"}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assessment Trigger
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={globalSettings.refiner_trigger_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      refiner_trigger_threshold: parseFloat(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(globalSettings.refiner_trigger_threshold * 100).toFixed(0)}%
                  </div>
                </div>
              </div>

              {/* Classification Settings */}
              <div className="bg-purple-50 rounded-lg p-5 space-y-5">
                <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  Classification
                </h3>

                {/* Cutoff Threshold */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cutoff Threshold
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.01"
                    value={globalSettings.cutoff_threshold}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      cutoff_threshold: parseFloat(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {(globalSettings.cutoff_threshold * 100).toFixed(0)}%
                  </div>
                </div>

                {/* Top Distances */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Top Distances (k-NN)
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="1"
                    value={globalSettings.top_distances}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      top_distances: parseInt(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {globalSettings.top_distances} distances
                  </div>
                </div>

                {/* Major Votes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Major Votes
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={globalSettings.major_votes}
                    onChange={(e) => setGlobalSettings({
                      ...globalSettings,
                      major_votes: parseInt(e.target.value)
                    })}
                    className="w-full settings-slider"
                  />
                  <div className="text-sm text-gray-600 mt-1">
                    {globalSettings.major_votes} votes required
                  </div>
                </div>

                {/* Show Uncertain Toggle */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Show Uncertain Detections
                    </label>
                    <button
                      type="button"
                      onClick={() => setGlobalSettings({
                        ...globalSettings,
                        show_uncertain_detections: !globalSettings.show_uncertain_detections
                      })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        globalSettings.show_uncertain_detections ? "bg-primary-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                          globalSettings.show_uncertain_detections ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">No settings found</p>
              <p className="text-gray-400 text-sm">Settings will be created automatically</p>
            </div>
          )}

          {/* Last Updated Info */}
          {globalSettings?.updated_at && (
            <div className="text-sm text-gray-500 text-center">
              Last updated: {new Date(globalSettings.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            fetchUsers();
          }}
        />
      )}

      {/* Create Room Modal */}
      {showCreateRoomModal && (
        <CreateRoomModal
          onClose={() => setShowCreateRoomModal(false)}
          onSuccess={() => {
            setShowCreateRoomModal(false);
            fetchRooms();
          }}
        />
      )}

      {/* Edit Room Modal */}
      {editingRoom && (
        <EditRoomModal
          room={editingRoom}
          onClose={() => setEditingRoom(null)}
          onSuccess={() => {
            setEditingRoom(null);
            fetchRooms();
          }}
        />
      )}

      {/* Create Section Modal */}
      {showCreateSectionModal !== null && (
        <CreateSectionModal
          roomId={showCreateSectionModal}
          availableClasses={availableClasses}
          onClose={() => setShowCreateSectionModal(null)}
          onSuccess={() => {
            fetchRoomSections(showCreateSectionModal);
            setShowCreateSectionModal(null);
          }}
          createSection={apiService.createSection}
        />
      )}

      {/* Edit Section Modal */}
      {editingSection && (
        <EditSectionModal
          section={editingSection}
          availableClasses={availableClasses}
          onClose={() => setEditingSection(null)}
          onSuccess={() => {
            if (editingSection) {
              // Find which room this section belongs to
              const roomId = Object.entries(roomSections).find(([_, sections]) =>
                sections.some((s) => s.id === editingSection.id)
              )?.[0];
              if (roomId) {
                fetchRoomSections(parseInt(roomId));
              }
            }
            setEditingSection(null);
          }}
        />
      )}

      {/* Import Layout Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Import Layout from CSV</h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportValidation(null);
                  setCsvContent("");
                  setImportError("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* File Upload */}
              {!importValidation && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Upload a CSV file to import rooms, sections, and expected items. The CSV should have the following columns:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
                    room_name,section_name,section_description,class_name,expected_quantity
                  </div>
                  <label className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors flex flex-col items-center justify-center">
                    <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload CSV file</span>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="absolute opacity-0 w-0 h-0"
                    />
                  </label>
                  {importLoading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Validating CSV...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Error */}
              {importError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="text-red-700">{importError}</p>
                  </div>
                </div>
              )}

              {/* Validation Results */}
              {importValidation && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className={`rounded-lg p-4 ${importValidation.valid ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"}`}>
                    <div className="flex items-center gap-2">
                      {importValidation.valid ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      )}
                      <span className={importValidation.valid ? "text-green-700" : "text-yellow-700"}>
                        {importValidation.valid_rows} of {importValidation.total_rows} rows are valid
                        {importValidation.errors?.length > 0 && ` (${importValidation.errors.length} errors)`}
                      </span>
                    </div>
                  </div>

                  {/* Errors List */}
                  {importValidation.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-medium text-red-800 mb-2">Validation Errors:</h4>
                      <ul className="space-y-1 text-sm text-red-700">
                        {importValidation.errors.map((err: any, idx: number) => (
                          <li key={idx}>
                            Row {err.row}: {err.field} "{err.value}" - {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Table */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Preview:</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Room</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Section</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importValidation.preview_data?.map((row: any, idx: number) => (
                              <tr
                                key={idx}
                                className={row.valid ? "bg-white" : "bg-red-50"}
                              >
                                <td className="px-4 py-2 text-sm">
                                  {row.valid ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">{row.room_name}</td>
                                <td className="px-4 py-2 text-sm text-gray-900">{row.section_name}</td>
                                <td className="px-4 py-2 text-sm text-gray-500 max-w-[150px] truncate">
                                  {row.section_description || "-"}
                                </td>
                                <td className={`px-4 py-2 text-sm ${row.class_valid === false ? "text-red-600 font-medium" : "text-gray-900"}`}>
                                  {row.class_name}
                                  {row.class_valid === false && (
                                    <span className="ml-1 text-xs">(not in catalog)</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">{row.expected_quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Import Options */}
                  {importValidation.valid_rows > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-3">
                        Ready to import {importValidation.valid_rows} valid rows. Invalid rows will be skipped.
                      </p>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleImportLayout(false)}
                          disabled={importLoading}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <FileUp className="h-4 w-4" />
                          {importLoading ? "Importing..." : "Add to Existing"}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("This will delete all existing rooms and sections. Are you sure?")) {
                              handleImportLayout(true);
                            }
                          }}
                          disabled={importLoading}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Replace All
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload Different File */}
                  <div className="text-center">
                    <label className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer inline-block">
                      Upload a different file
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="absolute opacity-0 w-0 h-0"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportValidation(null);
                  setCsvContent("");
                  setImportError("");
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Room Item Component with collapsible sections and drag-and-drop
interface RoomItemProps {
  room: Room;
  sections: RoomSection[];
  isExpanded: boolean;
  onToggle: () => void;
  onEditRoom: () => void;
  onDeleteRoom: () => void;
  onAddSection: () => void;
  onEditSection: (section: RoomSection) => void;
  onDeleteSection: (sectionId: number) => void;
  onRefreshSections: () => void;
  // Drag and drop
  draggingSectionId: number | null;
  dragOverRoomId: number | null;
  dragOverIndex: number | null;
  onDragStart: (e: React.DragEvent, sectionId: number, sourceRoomId: number) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, roomId: number, index: number) => void;
  onDrop: (e: React.DragEvent, targetRoomId: number, targetIndex: number) => void;
}

const RoomItem: React.FC<RoomItemProps> = ({
  room,
  sections,
  isExpanded,
  onToggle,
  onEditRoom,
  onDeleteRoom,
  onAddSection,
  onEditSection,
  onDeleteSection,
  draggingSectionId,
  dragOverRoomId,
  dragOverIndex,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => {
  const { getClassCategory, getClassThumbnail } = useCategoryContext();

  return (
    <li className="transition-colors">
      {/* Room Header */}
      <div
        className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="text-gray-400">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </div>
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Building className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900">{room.name}</h3>
            <p className="text-xs text-gray-500">
              {sections.length} section{sections.length !== 1 ? "s" : ""} •{" "}
              Created {new Date(room.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onAddSection}
            className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
            title="Add section"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={onEditRoom}
            className="text-indigo-600 hover:text-indigo-900 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
            title="Edit room"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={onDeleteRoom}
            className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="Delete room"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Sections List (collapsible) with drag-and-drop */}
      {isExpanded && (
        <div
          className={`bg-gray-50 border-t border-gray-200 ${
            draggingSectionId && dragOverRoomId === room.id ? "ring-2 ring-primary-300 ring-inset" : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            if (sections.length === 0) {
              onDragOver(e, room.id, 0);
            }
          }}
          onDrop={(e) => {
            if (sections.length === 0) {
              onDrop(e, room.id, 0);
            }
          }}
        >
          {sections.length === 0 ? (
            <div className={`px-6 py-8 text-center ${draggingSectionId ? "border-2 border-dashed border-primary-300 rounded-lg m-2" : ""}`}>
              <Layers className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {draggingSectionId ? "Drop section here" : "No sections yet"}
              </p>
              {!draggingSectionId && (
                <button
                  onClick={onAddSection}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Add first section
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {sections.map((section, index) => (
                <li
                  key={section.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, section.id, room.id)}
                  onDragEnd={onDragEnd}
                  onDragOver={(e) => onDragOver(e, room.id, index)}
                  onDrop={(e) => onDrop(e, room.id, index)}
                  className={`px-6 py-3 flex items-center gap-4 transition-all cursor-grab active:cursor-grabbing ${
                    draggingSectionId === section.id
                      ? "opacity-50 bg-gray-200"
                      : dragOverRoomId === room.id && dragOverIndex === index
                      ? "border-t-2 border-primary-500"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {/* Drag Handle */}
                  <div className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  {/* Section Thumbnail */}
                  <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded-lg overflow-hidden border border-gray-300">
                    {section.image_path ? (
                      <img
                        src={section.image_path}
                        alt={section.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <ImageIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Section Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {section.name}
                    </h4>
                    {section.description && (
                      <p className="text-xs text-gray-500 truncate">
                        {section.description}
                      </p>
                    )}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {section.expected_items.slice(0, 5).map((item, idx) => {
                        const category = getClassCategory(item.class_name);
                        const color = getClassColor(item.class_name, category);
                        const thumb = getClassThumbnail(item.class_name)?.url;
                        return (
                          <span
                            key={idx}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border"
                            style={{
                              borderColor: color,
                              backgroundColor: colorWithOpacity(color, 0.15),
                              color,
                            }}
                            title={`${formatClassNameForDisplay(item.class_name)} × ${item.expected_quantity}`}
                          >
                            <ThumbnailPreview className={item.class_name} thumbnailUrl={thumb}>
                              <span className="truncate max-w-[80px]">
                                {formatClassNameForDisplay(item.class_name).slice(0, 12)}
                                {formatClassNameForDisplay(item.class_name).length > 12 ? "…" : ""} ×{item.expected_quantity}
                              </span>
                            </ThumbnailPreview>
                          </span>
                        );
                      })}
                      {section.expected_items.length > 5 && (
                        <span className="text-xs text-gray-500">
                          +{section.expected_items.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Section Actions */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => onEditSection(section)}
                      className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded hover:bg-indigo-50 transition-colors"
                      title="Edit section"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDeleteSection(section.id)}
                      className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50 transition-colors"
                      title="Delete section"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

// Create User Modal Component
const CreateUserModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "user" as "admin" | "user",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await apiService.createUser(formData);
      onSuccess();
    } catch (error: any) {
      console.error("Failed to create user:", error);
      setError(error.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-96 shadow-xl rounded-lg bg-white">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Create New User
        </h3>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              placeholder="Enter username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              placeholder="Enter password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value as "admin" | "user",
                })
              }
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal: React.FC<{
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ user, onClose, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    role: user.role,
    is_active: user.is_active,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await apiService.updateUser(user.id, formData);
      onSuccess();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      setError(error.response?.data?.detail || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-96 shadow-xl rounded-lg bg-white">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Edit User</h3>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              placeholder="Enter username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value as "admin" | "user",
                })
              }
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              disabled={user.id === currentUser?.id}
            />
            <label
              htmlFor="is_active"
              className="ml-2 block text-sm text-gray-900"
            >
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Room Modal Component
const CreateRoomModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<RoomCreate>({ name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await apiService.createRoom(formData);
      onSuccess();
    } catch (error: any) {
      console.error("Failed to create room:", error);
      setError(error.response?.data?.detail || "Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-96 shadow-xl rounded-lg bg-white">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Create New Room
        </h3>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="room-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Room Name
            </label>
            <input
              type="text"
              id="room-name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Boutique Rue du Commerce"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Creating..." : "Create Room"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Room Modal Component
const EditRoomModal: React.FC<{
  room: Room;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ room, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<RoomCreate>({ name: room.name });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await apiService.updateRoom(room.id, formData);
      onSuccess();
    } catch (error: any) {
      console.error("Failed to update room:", error);
      setError(error.response?.data?.detail || "Failed to update room");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-96 shadow-xl rounded-lg bg-white">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Edit Room</h3>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="edit-room-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Room Name
            </label>
            <input
              type="text"
              id="edit-room-name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Enter room name"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Updating..." : "Update Room"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Section Modal Component
const EditSectionModal: React.FC<{
  section: RoomSection;
  availableClasses: string[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ section, availableClasses, onClose, onSuccess }) => {
  const { getClassCategory, getClassThumbnail } = useCategoryContext();
  const [formData, setFormData] = useState({
    name: section.name,
    description: section.description || "",
    expected_items: section.expected_items.map((item) => ({
      class_name: item.class_name,
      expected_quantity: item.expected_quantity,
    })),
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(section.image_path || null);
  const [imageChanged, setImageChanged] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  const classDropdownRef = React.useRef<HTMLDivElement>(null);
  const classDropdownMenuRef = React.useRef<HTMLDivElement>(null);
  const classDropdownButtonRef = React.useRef<HTMLButtonElement>(null);
  const [classDropdownPos, setClassDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [classQuery, setClassQuery] = useState("");
  const [classHighlight, setClassHighlight] = useState(0);

  // Close fullscreen image on any key press
  useEffect(() => {
    if (!showFullscreenImage) return;
    const handler = (e: KeyboardEvent) => setShowFullscreenImage(false);
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showFullscreenImage]);

  useEffect(() => {
    if (!classDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      const path = (e.composedPath && e.composedPath()) || [];
      const insideButton = path.includes(classDropdownRef.current as any);
      const insideMenu = path.includes(classDropdownMenuRef.current as any);
      if (!insideButton && !insideMenu) setClassDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [classDropdownOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("Image must be smaller than 5MB");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageChanged(true);
      setError("");
    }
  };

  const clearImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setImageFile(null);
    setImagePreview(null);
    setImageChanged(true);
  };

  const handleAddExpectedItem = (className: string) => {
    if (formData.expected_items.some((item) => item.class_name === className)) {
      return; // Already added
    }
    setFormData({
      ...formData,
      expected_items: [
        ...formData.expected_items,
        { class_name: className, expected_quantity: 1 },
      ],
    });
    setClassQuery("");
    setClassDropdownOpen(false);
  };

  const handleUpdateQuantity = (className: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    setFormData({
      ...formData,
      expected_items: formData.expected_items.map((item) =>
        item.class_name === className ? { ...item, expected_quantity: newQuantity } : item
      ),
    });
  };

  const handleRemoveExpectedItem = (className: string) => {
    setFormData({
      ...formData,
      expected_items: formData.expected_items.filter((item) => item.class_name !== className),
    });
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.expected_items.length === 0) {
      setError("Please add at least one expected item");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let imagePath: string | undefined = undefined;

      if (imageChanged) {
        if (imageFile) {
          imagePath = await fileToBase64(imageFile);
        } else {
          imagePath = null as any;
        }
      }

      const updateData: any = {
        name: formData.name,
        description: formData.description,
        expected_items: formData.expected_items,
      };

      if (imageChanged) {
        updateData.image_path = imagePath;
      }

      await apiService.updateSection(section.id, updateData);
      onSuccess();
    } catch (error: any) {
      console.error("Failed to update section:", error);
      setError(error.response?.data?.detail || "Failed to update section");
    } finally {
      setLoading(false);
    }
  };

  // Filter out already added classes from dropdown
  const availableForSelection = availableClasses.filter(
    (cls) => !formData.expected_items.some((item) => item.class_name === cls)
  );

  return (
    <>
      {/* Fullscreen Image Preview */}
      {showFullscreenImage && imagePreview && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-90 flex items-center justify-center cursor-pointer"
          onClick={() => setShowFullscreenImage(false)}
        >
          <img
            src={imagePreview}
            alt="Section preview"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <div className="absolute top-4 right-4 text-white text-sm opacity-60">
            Click anywhere or press any key to close
          </div>
        </div>,
        document.body
      )}

      {createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4 pb-24 md:pb-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] md:max-h-[90vh] flex flex-col">
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-900">Edit Section</h2>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Image, Name, Description */}
                <div className="space-y-4">
                  {/* Image Upload - Full width, clickable */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Image
                    </label>
                    {imagePreview ? (
                      <div
                        className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-primary-400 transition-colors"
                        onClick={() => setShowFullscreenImage(true)}
                      >
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={clearImage}
                          className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          Click to enlarge
                        </div>
                      </div>
                    ) : (
                      <label className="w-full aspect-video border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors">
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Upload section image</span>
                        <span className="text-xs text-gray-400 mt-1">Click to browse</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>

                  {/* Section Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Brief description of this section"
                    />
                  </div>
                </div>

                {/* Right Column: Expected Items */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Items <span className="text-red-500">*</span>
                    </label>

                    {/* Class Dropdown - adds on click */}
                    <div className="relative" ref={classDropdownRef}>
                      <button
                        type="button"
                        ref={classDropdownButtonRef}
                        onClick={() => {
                          if (!classDropdownOpen) {
                            const rect = classDropdownButtonRef.current!.getBoundingClientRect();
                            setClassDropdownPos({
                              top: rect.bottom + window.scrollY + 4,
                              left: rect.left + window.scrollX,
                              width: rect.width,
                            });
                          }
                          setClassDropdownOpen((v) => !v);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-500"
                      >
                        Select item class to add...
                      </button>
                      {classDropdownOpen &&
                        classDropdownPos &&
                        createPortal(
                          <div
                            ref={classDropdownMenuRef}
                            className="z-[9999] fixed bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                            style={{ top: classDropdownPos.top, left: classDropdownPos.left, width: classDropdownPos.width }}
                          >
                            <div className="p-2 border-b sticky top-0 bg-white">
                              <input
                                autoFocus
                                value={classQuery}
                                onChange={(e) => {
                                  setClassQuery(e.target.value);
                                  setClassHighlight(0);
                                }}
                                onKeyDown={(e) => {
                                  const filtered = fuzzyFilter(availableForSelection, classQuery);
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setClassHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
                                  }
                                  if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setClassHighlight((h) => Math.max(h - 1, 0));
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const pick = filtered[classHighlight] || filtered[0];
                                    if (pick) {
                                      handleAddExpectedItem(pick);
                                    }
                                  }
                                  if (e.key === "Escape") {
                                    setClassDropdownOpen(false);
                                  }
                                }}
                                placeholder="Type to search..."
                                className="w-full px-3 py-2 border rounded"
                              />
                            </div>
                            {fuzzyFilter(availableForSelection, classQuery).map((cls, idx) => {
                              const category = getClassCategory(cls);
                              const color = getClassColor(cls, category);
                              const thumb = getClassThumbnail(cls)?.url;
                              return (
                                <button
                                  key={cls}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                                    idx === classHighlight ? "bg-gray-100" : "hover:bg-gray-50"
                                  }`}
                                  onMouseEnter={() => setClassHighlight(idx)}
                                  onClick={() => handleAddExpectedItem(cls)}
                                >
                                  <span
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{
                                      borderColor: color,
                                      backgroundColor: colorWithOpacity(color, 0.15),
                                      color,
                                    }}
                                  >
                                    <ThumbnailPreview className={cls} thumbnailUrl={thumb}>
                                      <span className="truncate max-w-[200px]">{formatClassNameForDisplay(cls)}</span>
                                    </ThumbnailPreview>
                                  </span>
                                </button>
                              );
                            })}
                            {fuzzyFilter(availableForSelection, classQuery).length === 0 && (
                              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                No matching classes found
                              </div>
                            )}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>

                  {/* Expected Items Table */}
                  {formData.expected_items.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Qty</th>
                            <th className="px-3 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {formData.expected_items.map((item) => {
                            const category = getClassCategory(item.class_name);
                            const color = getClassColor(item.class_name, category);
                            const thumb = getClassThumbnail(item.class_name)?.url;
                            return (
                              <tr key={item.class_name} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <span
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                                    style={{
                                      borderColor: color,
                                      backgroundColor: colorWithOpacity(color, 0.15),
                                      color,
                                    }}
                                  >
                                    <ThumbnailPreview className={item.class_name} thumbnailUrl={thumb}>
                                      <span className="truncate max-w-[150px]">{formatClassNameForDisplay(item.class_name)}</span>
                                    </ThumbnailPreview>
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.expected_quantity}
                                    onChange={(e) => handleUpdateQuantity(item.class_name, parseInt(e.target.value) || 1)}
                                    className="w-full px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExpectedItem(item.class_name)}
                                    className="text-gray-400 hover:text-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500">
                      <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No items added yet</p>
                      <p className="text-xs mt-1">Select items from the dropdown above</p>
                    </div>
                  )}
                </div>
              </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {loading ? "Updating..." : "Update Section"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AdminPage;
